import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NetworkStatusMessages } from '@core/constants';
import { AlertService } from '@core/services/alert/alert.service';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { EnvironmentService } from '@core/services/environment.service';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { HotjarService } from '@core/services/hotjar/hotjar.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { LoadCountriesProps } from '@core/state/countries/countries.actions';
import { LoadStatesProps } from '@core/state/states/states.actions';
import { LoadTimezonesProps } from '@core/state/timezones/timezones.actions';
import {
  GetUserProfile,
  LoadIsCollapsed,
} from '@core/state/userProfile/userProfile.actions';
import { Store } from '@ngrx/store';
import { ToastComponent } from '@shipmercury/mercury-ui';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { GoogleMapsModule } from '@angular/google-maps';

import { getRoutes } from './routes';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, ToastComponent, ConfirmDialog, GoogleMapsModule],
})
export class AppComponent implements OnInit {
  authenticationService = inject(AuthenticationService);
  hotjarService = inject(HotjarService);
  alertService = inject(AlertService);
  envService = inject(EnvironmentService);
  analyticsService = inject(AnalyticsService);
  sessionStorageService = inject(SessionStorageService);

  isAuthenticated: boolean = false;
  mapApiLoaded!: boolean;
  initiatedWithImpersonation: boolean = false;

  constructor(
    private store: Store<any>,
    private router: Router,
    private featureFlagService: FeatureFlagService
  ) {
    this.initiatedWithImpersonation =
      window.location.href.includes('impersonate');

    const routes = getRoutes(this.featureFlagService);
    this.router.resetConfig(routes);

    addEventListener('storage', (event) => {
      if (event.key === 'auth-user') {
        this.sessionStorageService.delete('shipment-filter');
        location.href = '/';
      }
    });

    addEventListener('online', () => {
      this.alertService.success({ message: NetworkStatusMessages.online });
      document.body.style.pointerEvents = '';
    });

    addEventListener('offline', () => {
      this.alertService.error({
        message: NetworkStatusMessages.offline,
        life: 999999999,
      });
      document.body.style.pointerEvents = 'none';
    });
  }

  ngOnInit(): void {
    if (!this.initiatedWithImpersonation) {
      this.authenticationService.isAuthenticated().then((isAuthenticated) => {
        this.isAuthenticated = isAuthenticated;
        if (isAuthenticated) {
          this.store.dispatch(GetUserProfile());
          this.store.dispatch(LoadCountriesProps());
          this.store.dispatch(LoadStatesProps());
          this.store.dispatch(LoadTimezonesProps());
          this.store.dispatch(LoadIsCollapsed());
          this.initHotjar();
          this.featureFlagService.updateContext();
        }
      });
    }
    if (!this.mapApiLoaded) {
      this.loadGoogleAddressAutoComplete();
    }
    if (this.envService.googleAnalyticsId) {
      this.analyticsService.init();
    }
  }

  initHotjar() {
    this.hotjarService.initHotjar();
    this.hotjarService.identifyUser();
  }

  loadGoogleMap(): void {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.envService.googleMapApiKey}`;
    document.head.appendChild(script);
    script.onload = () => {
      this.mapApiLoaded = true;
    };
  }

  loadGoogleAddressAutoComplete(): void {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${this.envService.googleMapApiKey}`;
    document.head.appendChild(script);
    script.onload = () => {
      this.mapApiLoaded = true;
    };
  }
}
